"use client";

// Mobile hamburger + full-overlay menu, used below the md breakpoint.
// Plain links (Map, Compare) navigate directly; dropdown groups expand
// inline as accordions. The group containing the current route starts
// expanded so a visitor sees where they are.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { Icon } from "@/components/ui/icon";
import { NAV, isGroup, type NavGroup } from "@/components/nav-items";

export function MobileNav() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Body scroll lock while the overlay is up.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  const overlay =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            className="fixed inset-0 z-[100] flex flex-col bg-background/95 backdrop-blur-md"
            style={{ animation: "fade-in 160ms ease-out" }}
          >
            <div className="flex items-center justify-between border-b border-outline-variant/30 px-margin-mobile py-4">
              <span className="font-headline-md text-headline-md font-bold tracking-tight text-primary">
                Menu
              </span>
              <button
                type="button"
                onClick={close}
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Icon name="close" size={24} />
              </button>
            </div>
            <nav
              aria-label="Primary mobile overlay"
              className="flex flex-1 flex-col gap-1 overflow-y-auto px-margin-mobile py-6"
            >
              {NAV.map((node) =>
                isGroup(node) ? (
                  <MobileGroup key={node.label} group={node} onNavigate={close} />
                ) : (
                  <Link
                    key={node.label}
                    href={node.href}
                    onClick={close}
                    className="flex items-center justify-between rounded-xl px-4 py-4 font-headline-md text-headline-md text-on-surface no-underline transition-colors hover:bg-surface-container-low active:bg-surface-container"
                  >
                    <span>{node.label}</span>
                    <Icon name="chevron_right" size={20} className="text-on-surface-variant" />
                  </Link>
                ),
              )}
            </nav>
            <div className="border-t border-outline-variant/30 px-margin-mobile py-4 text-xs text-on-surface-variant">
              Toronto Parks Atlas · Civic Research Initiative
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        aria-expanded={open}
        className="flex h-10 w-10 items-center justify-center rounded-full text-on-surface hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hidden"
      >
        <Icon name="menu" size={24} />
      </button>
      {overlay}
    </>
  );
}

function MobileGroup({ group, onNavigate }: { group: NavGroup; onNavigate: () => void }) {
  const pathname = usePathname();
  // Start expanded if the current route lives inside this group, or is the
  // group's own landing page.
  const containsActive =
    (group.href && pathname === group.href) ||
    group.children.some((c) => pathname === c.href || pathname.startsWith(`${c.href}/`));
  const [expanded, setExpanded] = useState(containsActive);

  return (
    <div className="rounded-xl">
      <div className="flex items-center">
        {group.href ? (
          <Link
            href={group.href}
            onClick={onNavigate}
            className="flex-1 rounded-xl px-4 py-4 font-headline-md text-headline-md text-on-surface no-underline transition-colors hover:bg-surface-container-low active:bg-surface-container"
          >
            {group.label}
          </Link>
        ) : (
          <span className="flex-1 px-4 py-4 font-headline-md text-headline-md text-on-surface">
            {group.label}
          </span>
        )}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-label={`${expanded ? "Collapse" : "Expand"} ${group.label}`}
          className="flex h-12 w-12 items-center justify-center rounded-xl text-on-surface-variant hover:bg-surface-container-low focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Icon
            name="expand_more"
            size={24}
            className={`transition-transform duration-150 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {expanded && (
        <ul className="mb-1 ml-7 flex flex-col gap-0.5">
          {group.children.map((child) => (
            <li key={child.href}>
              <Link
                href={child.href}
                onClick={onNavigate}
                className="block rounded-lg px-4 py-3 font-body-lg text-body-lg text-on-surface-variant no-underline transition-colors hover:bg-surface-container-low hover:text-on-surface active:bg-surface-container"
              >
                {child.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
